import { Box, Flex, Icon, Input, Text, VStack, CloseButton } from "@chakra-ui/react";
import { useState } from "react";
import { FiUploadCloud } from "react-icons/fi";
import imageCompression from 'browser-image-compression';
import { useDispatch } from "react-redux";
import { addFile, removeFile } from "../../store/filesSlice";

function ImageUploader({ path, label = "Upload Images", isEdit, maxImages = 3 }) {
    const [images, setImages] = useState([]);
    const [error, setError] = useState("");
    const dispatch = useDispatch();

    const convertFileToUrl = async (file) => {
        try {
            let fileToUpload = file;
            if (file.type.startsWith('image/')) {
                const options = {
                    maxSizeMB: 1,
                    maxWidthOrHeight: 1920,
                    useWebWorker: true
                };
                fileToUpload = await imageCompression(file, options);
            }
            const uploadedFile = {
                name: fileToUpload.name,
                type: fileToUpload.type,
                file: fileToUpload,
            }
            dispatch(addFile({ key: path, file: uploadedFile }));
            const reader = new FileReader();
            reader.onloadend = () => {
                setImages((prev) => [...prev, reader.result]);
            };
            reader.readAsDataURL(fileToUpload);
        } catch (error) {
            console.error('Error during file upload:', error);
        }
    };

    const handleImageChange = (e) => {
        const files = Array.from(e.target.files);
        let newError = "";
        // console.log("Selected files:", files);
        // console.log("path:", path);
        if (images.length + files.length > maxImages) {
            newError = `You can only upload up to ${maxImages} images.`;
        } else {
            files.forEach((file) => {
                if (file.size > 2 * 1024 * 1024) {
                    newError = `${file.name} exceeds 2MB limit.`;
                } else {
                    convertFileToUrl(file);
                }
            });
        }

        setError(newError);
    };

    const handleRemoveImage = (index) => {
        dispatch(removeFile({ key: path, idx: index }));
        setImages(prev => prev.filter((_, idx) => idx !== index));
    };

    return (
        <VStack key={path} align="start" spacing={1} w="100%">
            {/* Title */}
            <Text fontWeight="bold">
                {label} <Text as="span" color="red.500">{isEdit ? "" : "*"}</Text>
                <Text as="span" fontWeight="normal" color="gray.500"> (Max {maxImages} images)</Text>
            </Text>

            {/* Upload Box */}
            <Box
                w="100%"
                minH="60px"
                border="2px dashed"
                borderColor="gray.300"
                borderRadius="md"
                p={2}
                key={path}
                overflow="hidden"
            >
                <Flex key={path} gap={4} wrap="wrap" justify="center" align="center">
                    {images.map((src, idx) => (
                        <Box key={path + idx} position="relative" boxSize="70px">
                            <img
                                src={src}
                                alt={`${label} ${idx + 1}`}
                                style={{ width: "100%", height: "100%", objectFit: "cover", borderRadius: "8px" }}
                            />
                            <CloseButton
                                size="xs"
                                position="absolute"
                                top="0"
                                right="0"
                                color="white"
                                bg="red.500"
                                borderRadius="full"
                                _hover={{ bg: "red.600" }}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleRemoveImage(idx);
                                }}
                            />
                        </Box>
                    ))}

                    {/* Upload Icon */}
                    {images.length < maxImages && (
                        <Box
                            as="label"
                            key={path}
                            htmlFor="image-upload"
                            cursor="pointer"
                        >
                            <Flex
                                key={path}
                                direction="column"
                                align="center"
                                justify="center"
                                boxSize="50px"
                                border="1px dashed gray"
                                borderRadius="8px"
                                color="gray.400"
                            >
                                <Icon as={FiUploadCloud} boxSize={5} />
                                <Text fontSize="xs">Upload</Text>
                            </Flex>
                        </Box>
                    )}
                </Flex>
            </Box>

            {/* Hidden Input */}
            <Input
                type="file"
                id="image-upload"
                accept="image/jpeg, image/png"
                multiple
                onChange={handleImageChange}
                display="none"
            />

            {/* Error Message */}
            {error && (
                <Text color="red.500" fontSize="sm">
                    {error}
                </Text>
            )}
        </VStack>
    );
}

export default ImageUploader;
